import { Alert, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FactoryActionParamForm, parseFactoryParams } from "./FactoryActionForm";
import FlussNodePanelShell from "./FlussNodePanelShell";
import { ActivityPanelProps, NodeNameField } from "./ActivityNodePanels";
import { referenceId } from "./fluss-node-panel-data";
import { primaryBindingFor, updateBinding } from "./fluss-node-panel-utils";
import { RecordSourceBindingField } from "./RecordSourceField";

const SEND_EMAIL_ACTION_NAME = "send_email";

const SendTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const binding = primaryBindingFor(selection.businessObject);
    const action = data.factoryActions.find((candidate) => candidate.name === SEND_EMAIL_ACTION_NAME);
    const [params, setParams] = useState<Record<string, any>>(parseFactoryParams(binding?.params));

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setParams(parseFactoryParams(current?.params));
        if (!action) return;

        const businessRule = referenceId(action.business_rule);
        if (current?.actionId === action.id && referenceId(current?.businessRule) === businessRule) return;

        updateBinding(selection, modeler, "service", {
            actionId: action.id,
            businessRule,
            params: current?.params || "{}",
        });
        onChanged();
    }, [action, modeler, onChanged, selection]);

    const updateActionParams = (next: Record<string, any>) => {
        setParams(next);
        updateBinding(selection, modeler, "service", { params: JSON.stringify(next) });
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="send_task"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "send",
                label: t("fluss.node_panel.tab.send", { defaultValue: "Send" }),
                content: (
                    <Stack spacing={1.25}>
                        <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
                        <RecordSourceBindingField selection={selection} modeler={modeler} kind="service" readOnly={readOnly} onChanged={onChanged} />
                        <Alert severity="warning">
                            {t("fluss.node_panel.send_task.correlation_warning", {
                                defaultValue: "The platform automatically adds a unique identifier to the end of the email body. Do not remove it or hide it in a clipped footer, or a future Wait for email node will not be able to match the reply.",
                            })}
                        </Alert>
                        {!action && (
                            <Alert severity="error">
                                {t("fluss.node_panel.send_task.missing_action", { defaultValue: "The send_email factory action is not available." })}
                            </Alert>
                        )}
                        {action && (
                            <FactoryActionParamForm
                                action={action}
                                params={params}
                                data={data}
                                hideDeprecatedTemplateValue
                                disabled={readOnly}
                                onChange={updateActionParams}
                            />
                        )}
                    </Stack>
                ),
            }]}
        />
    );
};

export default SendTaskNodePanel;
